import React, { useContext, useMemo } from "react";
import { Combobox } from "@axum/ax-design";
import { languageContext } from "../../../../config/language";
import { filterGroupClass } from "../Filters.styles";
import { useFilterStatus } from "../../../../config/appliedFilters";

// types
import { Origin } from "../../../../types/origins";
import { Leader } from "../../../../types/leaders";
import { Supervisor } from "../../../../types/supervisors";
import { Seller } from "../../../../types/sellers";
import { State } from "../../../../types/states";
import { Channel } from "../../../../types/channels";
import { Client } from "../../../../types/clients";

import {OperationType} from "../../../../types/operationTypes";

interface Props {
  // Business Filters
  idSourceFilter: string;
  setIdSourceFilter: (value: string) => void;
  originsSelected: Origin[];
  leadersSelected: any[];
  supervisorsSelected: any[];
  sellersSelected: any[];
  statesSelected: State[];
  channelsSelected: Channel[];
  clientsSelected: Client[];
  operationTypesSelected: OperationType[];
  setOriginsSelected: (param: Origin[]) => void;
  setLeadersSelected: (param: any[]) => void;
  setSupervisorsSelected: (param: any[]) => void;
  setSellersSelected: (param: any[]) => void;
  setStatesSelected: (param: State[]) => void;
  setChannelsSelected: (param: Channel[]) => void;
  setClientsSelected: (param: Client[]) => void;
  setOperationTypesSelected: (param: OperationType[]) => void;
  sendFilters: () => void;
  resetFilters: () => void;

  // redux
  origins: Origin[];
  fetchingOriginsList: boolean;
  leaders: Leader[];
  fetchingLeadersList: boolean;
  supervisors: Supervisor[];
  fetchingSupervisorsList: boolean;
  sellers: Seller[];
  fetchingSellersList: boolean;
  states: State[];
  fetchingStatesList: boolean;
  channels: Channel[];
  fetchingChannelsList: boolean;
  clients: Client[];
  fetchingClientsList: boolean;
  operationTypes: OperationType[];
  fetchingOperationTypesList: boolean;
}

interface FilterProps {
  inputId: string;
  optionList: any[];
  dataItemKey: string;
  optionName: string;
  values: any[];
  handleChange: (updatedList: any[]) => void;
  dataCy?: string;
}

const FilterSelect = (filter: FilterProps) => {
  const { optionList, dataItemKey, optionName } = filter;
  const keyOf = (item: any) => String(item[dataItemKey]);
  const options = useMemo(
    () => (optionList || []).map(item => ({ value: String(item[dataItemKey]), label: String(item[optionName]) })),
    [optionList, dataItemKey, optionName]
  );
  const change = (keys: string[]) => filter.handleChange((optionList || []).filter(item => keys.includes(keyOf(item))));
  return (
    <Combobox
      multiple
      id={filter.inputId}
      options={options}
      value={filter.values.map(keyOf)}
      onChange={change}
      placeholder="Todos"
      data-cy={filter.dataCy}
    />
  );
};

export default (props: Props) => {
  const { languageContext: lang } = useContext(languageContext);
  const statusOf = useFilterStatus();

  const filters_first_row = [
    {
      filter_code: "origin",
      valuesKey: "originsSelected",
      placeholder: "Origen",
      optionList: props.origins,
      dataItemKey: "shortDescription",
      optionName: "description",
      inputId: "input_origins",
      values: props.originsSelected,
      handleChange: props.setOriginsSelected,
      disabled: Boolean(
        props.fetchingOriginsList
      ),
      loading: props.fetchingOriginsList,
      dataCy: 'selector-origin',
    },
    // todo pilas comments
    {
      filter_code: "leader",
      valuesKey: "leadersSelected",
      placeholder: lang.leader,
      optionList: props.leaders.sort((a, b) => parseInt(a.leaderId) - parseInt(b.leaderId)),
      dataItemKey: "leaderId",
      optionName: "joined",
      inputId: "input_leaders",
      values: props.leadersSelected,
      handleChange: props.setLeadersSelected,
      disabled: Boolean(
          props.fetchingLeadersList
      ),
      loading: props.fetchingLeadersList,
      dataCy: 'selector-leader',
    },
    {
      filter_code: "supervisor",
      valuesKey: "supervisorsSelected",
      placeholder: lang.supervisor,
      optionList: props.supervisors.sort((a, b) => parseInt(a.supervisorId) - parseInt(b.supervisorId)),
      dataItemKey: "supervisorId",
      optionName: "joined",
      inputId: "input_supervisors",
      values: props.supervisorsSelected,
      handleChange: props.setSupervisorsSelected,
      disabled: Boolean(
        props.fetchingSupervisorsList
      ),
      loading: props.fetchingSupervisorsList,
      dataCy: 'selector-supervisor',
    },
    {
      filter_code: "seller",
      valuesKey: "sellersSelected",
      placeholder: lang.seller,
      optionList: props.sellers.sort((a, b) => parseInt(a.sellerId) - parseInt(b.sellerId)),
      dataItemKey: "sellerId",
      optionName: "joined",
      inputId: "input_seller",
      values: props.sellersSelected,
      handleChange: props.setSellersSelected,
      disabled: Boolean(
        props.fetchingSellersList
      ),
      loading: props.fetchingSellersList,
      dataCy: 'selector-seller',
    },
  ];
  const filters_second_row = [
    {
      filter_code: "state",
      valuesKey: "statesSelected",
      placeholder: 'Estado',
      optionList: props.states,
      dataItemKey: "state",
      optionName: "state",
      inputId: "input_state",
      values: props.statesSelected,
      handleChange: props.setStatesSelected,
      disabled: Boolean(
        props.fetchingStatesList
      ),
      loading: props.fetchingStatesList,
      dataCy: 'selector-state',
    },
    {
      filter_code: "channel",
      valuesKey: "channelsSelected",
      placeholder: 'Canal',
      optionList: props.channels,
      dataItemKey: "clientChannel",
      optionName: "clientChannel",
      inputId: "input_channel",
      values: props.channelsSelected,
      handleChange: props.setChannelsSelected,
      disabled: Boolean(
        props.fetchingChannelsList
      ),
      loading: props.fetchingChannelsList,
      dataCy: 'selector-channel',
    },
    {
      filter_code: "client",
      valuesKey: "clientsSelected",
      placeholder: 'Cliente',
      optionList: props.clients.sort((a, b) => parseInt(a.clientId) - parseInt(b.clientId)),
      dataItemKey: "clientId",
      optionName: "joined",
      inputId: "input_client",
      values: props.clientsSelected,
      handleChange: props.setClientsSelected,
      disabled: Boolean(
        props.fetchingClientsList
      ),
      loading: props.fetchingClientsList,
      dataCy: 'selector-client',
    },
    {
      filter_code: "operation_types",
      valuesKey: "operationTypesSelected",
      placeholder: 'Tipo de operación',
      optionList: props.operationTypes.sort((a, b) => parseInt(a.axumOperationTypeCode) - parseInt(b.axumOperationTypeCode)),
      dataItemKey: "axumOperationTypeCode",
      optionName: "joined",
      inputId: "input_operation_types",
      values: props.operationTypesSelected,
      handleChange: props.setOperationTypesSelected,
      disabled: Boolean(
          props.fetchingOperationTypesList
      ),
      loading: props.fetchingOperationTypesList,
      dataCy: 'selector-operation-types',
    },
  ];

  return (
    <>
      {[...filters_first_row, ...filters_second_row].map((filter: any) => (
        <div className={filterGroupClass(statusOf(filter.valuesKey))} key={filter.filter_code}>
          <label className="ax-filter-group-label" htmlFor={filter.inputId}>{filter.placeholder}</label>
          <FilterSelect {...filter} />
        </div>
      ))}
      <div className={filterGroupClass(statusOf("idSourceFilter"))}>
        <label className="ax-filter-group-label" htmlFor="input_id_source">ID Source</label>
        <input
          id="input_id_source"
          type="text"
          className="ax-form-control"
          placeholder="Ej.: MSU-88231"
          value={props.idSourceFilter}
          onChange={e => props.setIdSourceFilter(e.target.value)}
          onKeyDown={e => (e.key === "Enter" ? props.sendFilters() : undefined)}
          data-cy="input-id-source"
        />
      </div>
    </>
  );
};
